if (!customElements.get('product-slider')) {
  class ProductSlider extends HTMLElement {
    constructor() {
      super();
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.isDesktop = window.innerWidth >= 1100;
      this.currentSlide = 1;
      this.totalSlides = 0;
      this.lastHeaderHeight = null;
    }

    connectedCallback() {
      this.init();
      this.setupResizeListener();
      this.watchHeaderHeight();
    }

    init() {
      this.isDesktop = window.innerWidth >= 1100;
      this.totalSlides = this.querySelectorAll('.product-slider__mobile .swiper-slide').length;
      
      if (this.isDesktop) {
        this.initDesktopSlider();
      } else {
        this.initMobileSlider();
      }

      this.updateHeaderHeight();
    }

    initDesktopSlider() {
      const thumbsContainer = this.querySelector('.product-slider__desktop .product-slider__thumbs');
      const mainContainer = this.querySelector('.product-slider__desktop .product-slider__main');
      
      if (!thumbsContainer || !mainContainer) {
        console.error('Desktop slider containers not found');
        return;
      }

      // Initialize thumbs slider first
      this.thumbsSwiper = new Swiper(thumbsContainer, {
        direction: 'vertical',
        slidesPerView: 5,
        spaceBetween: 8,
        navigation: {
          nextEl: this.querySelector('.thumbs-button-next'),
          prevEl: this.querySelector('.thumbs-button-prev'),
        },
        on: {
          slideChange: () => {
            if (this.mainSwiper) {
              this.mainSwiper.slideTo(this.thumbsSwiper.activeIndex);
            }
          }
        }
      });

      // Initialize main slider with thumbs connection
      this.mainSwiper = new Swiper(mainContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        thumbs: {
          swiper: this.thumbsSwiper
        },
        on: {
          slideChange: () => {
            if (this.thumbsSwiper) {
              this.thumbsSwiper.slideTo(this.mainSwiper.activeIndex);
            }
            this.updateSlideCounter();
          }
        }
      });
    }

    initMobileSlider() {
      const mainContainer = this.querySelector('.product-slider__mobile .product-slider__main');
      
      if (!mainContainer) {
        console.error('Mobile slider container not found');
        return;
      }

      this.mainSwiper = new Swiper(mainContainer, {
        slidesPerView: 1,
        spaceBetween: 10,
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev'),
        },
        on: {
          slideChange: () => {
            this.updateSlideCounter();
          }
        }
      });

      this.updateSlideCounter();
    }

    updateSlideCounter() {
      const counter = this.querySelector('.slide-counter');
      if (counter) {
        this.currentSlide = this.mainSwiper.activeIndex + 1;
        const currentFormatted = this.currentSlide.toString().padStart(2, '0');
        const totalFormatted = this.totalSlides.toString().padStart(2, '0');
        counter.innerHTML = `<span class="slide-counter__current">${currentFormatted}</span> <span class="slide-counter__sep">/</span> <span class="slide-counter__total">${totalFormatted}</span>`;
      }
    }

    setupResizeListener() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const wasDesktop = this.isDesktop;
          this.isDesktop = window.innerWidth >= 1100;
          
          if (wasDesktop !== this.isDesktop) {
            this.destroySliders();
            this.init();
          }
        }, 250);
      });
    }

    getHeaderSections() {
      return [
        document.querySelector('#shopify-section-header'),
        document.querySelector('#shopify-section-announcement-bar')
      ].filter(Boolean);
    }

    updateHeaderHeight() {
      const totalHeight = this.getHeaderSections().reduce((height, section) => height + section.offsetHeight, 0);

      // Set the CSS variable on the document root
      if (totalHeight !== this.lastHeaderHeight) {
        this.lastHeaderHeight = totalHeight;
        document.documentElement.style.setProperty('--header-total-height', `${totalHeight}px`);
      }

      return totalHeight;
    }

    watchHeaderHeight() {
      // The custom overrides and swiper stylesheets load deferred (media="print" onload),
      // so on an uncached load the header is measured while still partly unstyled and
      // reports a much taller height than it ends up with. That inflated value is baked
      // into --header-total-height and shrinks the gallery for the rest of the page life.
      // Re-measure whenever the header actually changes height.
      const sections = this.getHeaderSections();

      if (sections.length && 'ResizeObserver' in window) {
        this.headerObserver = new ResizeObserver(() => this.updateHeaderHeight());
        sections.forEach((section) => this.headerObserver.observe(section));
      }

      // A page opened in a background tab lays out and runs its observers while hidden,
      // so re-measure once it is actually shown and painted.
      this.remeasureHeader = () => requestAnimationFrame(() => this.updateHeaderHeight());

      this.onWindowLoad = this.remeasureHeader;
      window.addEventListener('load', this.onWindowLoad);

      this.onPageShow = this.remeasureHeader;
      window.addEventListener('pageshow', this.onPageShow);

      this.onVisibilityChange = () => {
        if (!document.hidden) this.remeasureHeader();
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (document.fonts) {
        document.fonts.ready.then(() => this.remeasureHeader());
      }
    }

    destroySliders() {
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }
    }

    disconnectedCallback() {
      this.destroySliders();

      if (this.headerObserver) {
        this.headerObserver.disconnect();
        this.headerObserver = null;
      }
      if (this.onWindowLoad) {
        window.removeEventListener('load', this.onWindowLoad);
        this.onWindowLoad = null;
      }
      if (this.onPageShow) {
        window.removeEventListener('pageshow', this.onPageShow);
        this.onPageShow = null;
      }
      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.onVisibilityChange = null;
      }
    }
  }

  customElements.define('product-slider', ProductSlider);
}